import type { CustomRequestConfig } from '@mathflat/domain/@entities/Auth/axiosJwtSetup'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { isToday } from 'date-fns'
import { isArray } from 'lodash-es'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { LOGIN_API_URLS } from '@/constant'
import { CommonError } from '@/dto/common/CommonError'
import ServerMaintenanceService from '@/page/etc/ServerMaintenance/_service/ServerMaintenance.service'
import { localStorageService } from '@/service/storage.service'
import WebviewService from '@/service/webview.service'
import type { ResErr } from '@/types/common/api'
import { deleteNullField, emptyStringToNull } from '@/utils/common'
import { envUtils } from '@/utils/envUtils'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from '../jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isStaging) {
    return `https://api-staging.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isLive) {
    return `https://api.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://api-dev.${domainProvider.TARGET_DOMAIN}`
})()

/**
 * Teacher 앱 전용 meta 옵션을 추가한 CustomRequestConfig
 */
interface TeacherCustomRequestConfig extends CustomRequestConfig {
  meta?: {
    useAbortController: boolean
    customUniqueKey?: string
  }
}

export type CustomRequestConfigMeta = {
  useAbortController: boolean
  customUniqueKey?: string
}

const preventDuplicateRequest = (
  (
    request: Record<
      string,
      { abortController: AbortController; prevConfigData: TeacherCustomRequestConfig }
    >,
  ) =>
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (!(config as TeacherCustomRequestConfig).meta?.useAbortController) return config

    const method = config.method?.toUpperCase() ?? 'GET'
    const url = config.url?.split('?')[0] ?? ''

    const requestKey = (config as TeacherCustomRequestConfig).meta?.customUniqueKey || method + url
    const currentRequest = request[requestKey]

    if (currentRequest) {
      currentRequest.abortController.abort()
    }

    request[requestKey] = {
      abortController: new AbortController(),
      prevConfigData: config as TeacherCustomRequestConfig,
    }

    return {
      ...config,
      signal: request[requestKey].abortController.signal,
    }
  }
)({})

const maxios: AxiosInstance & {
  get<T = any, R = AxiosResponse<T>>(url: string, config?: TeacherCustomRequestConfig): Promise<R>
  post<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  put<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  patch<T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: TeacherCustomRequestConfig,
  ): Promise<R>
  delete<T = any, R = T, D = any>(url: string, config?: TeacherCustomRequestConfig): Promise<R>
} = axios.create({
  baseURL: BASE_URL,
  headers: {
    'content-Type': 'application/json',
  },

  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

export const getXPlatform = () => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
}

maxios.defaults.headers.common['x-platform'] = getXPlatform()
maxios.defaults.headers.common['x-freewheelin-host'] = domainProvider.TARGET_DOMAIN

maxios.interceptors.request.use(
  wrapRequestInterceptorWithJwt((config) => {
    //오늘 처음 쓰는 경우 - localstorage에 기록하고 ping 보내기 (login api, whalespace는 제외)
    if (
      !Object.values(LOGIN_API_URLS).includes(config.url || '') &&
      !config.url?.includes('/password') &&
      !/\/academies\/D\d+\/subscription-offer/.test(config.url || '')
    ) {
      const todayFirstUseHistory = localStorageService.getItem('TODAY_FIRST_USE_HISTORY')
      if (!todayFirstUseHistory || !isToday(new Date(todayFirstUseHistory))) {
        localStorageService.setItem('TODAY_FIRST_USE_HISTORY')(new Date().getTime())
      }
    }

    if (config.data instanceof FormData) {
      return preventDuplicateRequest(config)
    }

    // data에서 emptyString("")은 null로 보냄
    if (!isArray(config.data) && isObjectLike(config.data)) {
      config.data = emptyStringToNull(config.data)
    }

    return preventDuplicateRequest(config)
  }),
)

maxios.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data.error) {
      window.alert(response.data.error)
      return Promise.reject(response.data.error)
    }
    
    return response
  },
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>

      if (axiosError.code === 'ERR_CANCELED') return Promise.reject(axiosError)

      if (axiosError.response) {
        const {
          config,
          data: { message, code, data },
          status,
        } = axiosError.response

        const err = new CommonError(message, { type: 'API', status, code, data, config })

        if (status >= 501) {
          await ServerMaintenanceService.checkServerMaintenance()
        }

        return Promise.reject<CommonError>(err)
      } else {
        const err = new CommonError(axiosError.message, { type: 'CORS' })
        return Promise.reject<CommonError>(err)
      }
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return maxios(originalRequest)
    },
  ),
)

export const getResource = async (url: string) => {
  const response = await axios.get(url)
  if (response.status !== 200) throw new Error(response.statusText)
  return response.data
}

export default maxios
